/**
 * Sortable / plain column headers shared by the list tables (Submission
 * Triage, Policies).
 *
 * The sort directive is the FastAPI/DRF string the list endpoints take:
 * `field` ascending, `-field` descending. Clicking cycles
 * none → asc → desc → none.
 */
import { ArrowDown, ArrowUp, ChevronSelectorVertical } from "@untitledui/icons";
import { cx } from "@tricura/atlas-ui";

export type SortDir = "asc" | "desc";

// eslint-disable-next-line react-refresh/only-export-components -- helper co-located with the header it serves
export function parseSort(sort: string | undefined): { field: string; dir: SortDir } | null {
    if (!sort) return null;
    if (sort.startsWith("-")) return { field: sort.slice(1), dir: "desc" };
    return { field: sort, dir: "asc" };
}

export function SortHeader({
    field,
    sort,
    onSortChange,
    children,
}: {
    field: string;
    sort: string | undefined;
    onSortChange: (next: string | undefined) => void;
    children: React.ReactNode;
}) {
    const parsed = parseSort(sort);
    const active = parsed?.field === field;
    const dir = active ? parsed!.dir : null;

    const cycle = () => {
        if (!active) onSortChange(field);
        else if (dir === "asc") onSortChange(`-${field}`);
        else onSortChange(undefined);
    };

    const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ChevronSelectorVertical;
    const ariaSort = !active ? "none" : dir === "asc" ? "ascending" : "descending";

    return (
        <button
            type="button"
            onClick={cycle}
            aria-sort={ariaSort}
            className={cx(
                "hover:text-primary inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded text-xs font-semibold transition-colors",
                active ? "text-primary" : "text-quaternary",
            )}
        >
            {children}
            <Icon aria-hidden="true" className={cx("size-3 stroke-[2.25px]", active ? "text-fg-secondary" : "text-fg-quaternary")} />
        </button>
    );
}

export function PlainHeader({ children }: { children: React.ReactNode }) {
    return <span className="text-quaternary whitespace-nowrap text-xs font-semibold">{children}</span>;
}
